// src/components/groupqm/GroupSelfJoin.tsx — Sign-In System (Build 2) self-join
// surface for the Me page. Lists self-joinable chat groups the user can join,
// and their existing self-joinable memberships (with a leave control).
//
// Uses the app's runtime chrome tokens (C/F + ui Card/Button) rather than the
// net-new heather lane tokens, so it sits naturally among the other Me-page
// cards (which already use C.heather* accents). Gated on the Group QM surface
// rule from the kickoff: me.features.groupQuickMessage && me.signIn.eligible.
// Renders nothing when there are no self-joinable groups to show.

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../../api";
import type { MeResponse } from "../../auth";
import { C, F } from "../../theme";
import { Button, Card, Eyebrow } from "../ui";

interface AvailableGroup {
  id: number;
  code: string;
  displayName: string;
  description: string | null;
  memberCount: number;
}
interface MyGroupLite {
  id: number;
  displayName: string;
  isSelfJoinable: boolean;
}

export function GroupSelfJoin({ me }: { me: MeResponse }) {
  const enabled = me.features?.groupQuickMessage !== false && !!me.signIn?.eligible;
  const navigate = useNavigate();
  const [available, setAvailable] = useState<AvailableGroup[] | null>(null);
  const [mine, setMine] = useState<MyGroupLite[] | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const [a, m] = await Promise.all([
        apiFetch<{ groups: AvailableGroup[] }>("/api/group-qm/available"),
        apiFetch<{ groups: MyGroupLite[] }>("/api/group-qm/me"),
      ]);
      setAvailable(a.groups);
      setMine(m.groups.filter((g) => g.isSelfJoinable));
    } catch {
      setAvailable([]);
      setMine([]);
    }
  }, []);

  useEffect(() => {
    if (enabled) void load();
  }, [enabled, load]);

  if (!enabled || !available || !mine) return null;
  if (available.length === 0 && mine.length === 0) return null;

  const join = async (id: number) => {
    setBusyId(id);
    try { await apiFetch(`/api/group-qm/${id}/join`, { method: "POST" }); await load(); }
    catch { /* surfaced on next load */ }
    finally { setBusyId(null); }
  };
  const leave = async (id: number) => {
    setBusyId(id);
    try { await apiFetch(`/api/group-qm/${id}/membership`, { method: "DELETE" }); await load(); }
    catch { /* surfaced on next load */ }
    finally { setBusyId(null); }
  };

  return (
    <Card>
      <Eyebrow>Group messages</Eyebrow>
      <p style={{ fontFamily: F.body, fontSize: 13, color: C.inkSoft, margin: "6px 0 12px", lineHeight: 1.5 }}>
        Join the chat groups you take part in. You'll see new messages on Home while you're signed in for duty.
      </p>

      {mine.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: available.length > 0 ? 14 : 0 }}>
          {mine.map((g) => (
            <div key={g.id} style={{
              display: "flex", alignItems: "center", gap: 10, padding: "10px 12px",
              background: C.heatherBg, border: `1px solid ${C.heatherLt}`, borderRadius: 12,
            }}>
              <button
                type="button"
                onClick={() => navigate(`/group-qm/${g.id}`)}
                style={{ flex: 1, minWidth: 0, textAlign: "left", background: "transparent", border: "none", cursor: "pointer", padding: 0 }}
              >
                <span style={{ display: "block", fontFamily: F.serif, fontSize: 15, fontWeight: 700, color: C.ink }}>{g.displayName}</span>
                <span style={{ display: "block", fontFamily: F.body, fontSize: 11.5, fontWeight: 700, color: C.heatherDk, marginTop: 1 }}>Joined · tap to open</span>
              </button>
              <Button variant="secondary" disabled={busyId === g.id} onClick={() => leave(g.id)}>
                {busyId === g.id ? "…" : "Leave"}
              </Button>
            </div>
          ))}
        </div>
      )}

      {available.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {available.map((g) => (
            <div key={g.id} style={{
              display: "flex", alignItems: "center", gap: 10, padding: "10px 12px",
              background: C.card, border: `1px solid ${C.hairline}`, borderRadius: 12,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: F.serif, fontSize: 15, fontWeight: 700, color: C.ink }}>{g.displayName}</div>
                {g.description && (
                  <div style={{ fontFamily: F.body, fontSize: 12, color: C.inkSoft, marginTop: 2, lineHeight: 1.4 }}>{g.description}</div>
                )}
                <div style={{ fontFamily: F.mono, fontSize: 10.5, color: C.inkMute, marginTop: 2 }}>{g.memberCount} members</div>
              </div>
              <Button variant="primary" disabled={busyId === g.id} onClick={() => join(g.id)}>
                {busyId === g.id ? "…" : "Join"}
              </Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
